import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { Result } from '../models/result';
import { toAppError } from '../utils/error.util';
import {
  Plan,
  OrganizationWithPlan,
  SubscribePayload,
  Subscription,
  BreakdownPayload,
  SubscriptionBreakdown,
  AddSeatsPayload,
  DecreaseSeatsPayload,
} from './plans.state';

@Injectable({ providedIn: 'root' })
export class PlansApi {
  readonly #http = inject(HttpClient);
  readonly #baseUrl = `${environment.apiUrl}account`;

  getCurrentOrganization(): Observable<Result<OrganizationWithPlan>> {
    return this.#http
      .get<OrganizationWithPlan>(`${this.#baseUrl}/organizations/current`)
      .pipe(
        map(data => ({ ok: true as const, data })),
        catchError(err => of({ ok: false as const, error: toAppError(err) })),
      );
  }

  listPlans(): Observable<Result<Plan[]>> {
    return this.#http.get<Plan[]>(`${this.#baseUrl}/plans/`).pipe(
      map(data => ({ ok: true as const, data })),
      catchError(err => of({ ok: false as const, error: toAppError(err) })),
    );
  }

  getSubscription(organizationId: number): Observable<Result<Subscription>> {
    const url = `${this.#baseUrl}/organizations/${organizationId}/subscription`;
    return this.#http.get<Subscription>(url).pipe(
      map(data => ({ ok: true as const, data })),
      catchError(err => of({ ok: false as const, error: toAppError(err) })),
    );
  }


  /** Priced breakdown (unit price, subtotal, surcharge, tax, total) for a prospective plan/seat change — doesn't commit anything. */
  getBreakdown(
    organizationId: number,
    payload: BreakdownPayload,
  ): Observable<Result<SubscriptionBreakdown>> {
    const url = `${environment.apiUrl}payment/organizations/${organizationId}/subscription/breakdown`;
    return this.#http.post<SubscriptionBreakdown>(url, payload).pipe(
      map(data => ({ ok: true as const, data })),
      catchError(err => of({ ok: false as const, error: toAppError(err) })),
    );
  }

  /** Subscribe the organization to a plan and pay with a Collect.js token. */
  createSubscription(
    organizationId: number,
    payload: SubscribePayload,
  ): Observable<Result<void>> {
    const url = `${environment.apiUrl}account/organizations/${organizationId}/subscription`;
    return this.#http.post<void>(url, payload).pipe(
      map(() => ({ ok: true as const, data: undefined })),
      catchError(err => of({ ok: false as const, error: toAppError(err) })),
    );
  }

  /** Add seats to the subscription — each seat billed at full plan price. */
  addSeats(organizationId: number, payload: AddSeatsPayload): Observable<Result<void>> {
    const url = `${this.#baseUrl}/organizations/${organizationId}/subscription/users/add`;
    return this.#http.post<void>(url, payload).pipe(
      map(() => ({ ok: true as const, data: undefined })),
      catchError(err => of({ ok: false as const, error: toAppError(err) })),
    );
  }

  /** Decrease seats on the subscription. */
  decreaseSeats(organizationId: number, payload: DecreaseSeatsPayload): Observable<Result<void>> {
    const url = `${this.#baseUrl}/organizations/${organizationId}/subscription/users/decrease`;
    return this.#http.post<void>(url, payload).pipe(
      map(() => ({ ok: true as const, data: undefined })),
      catchError(err => of({ ok: false as const, error: toAppError(err) })),
    );
  }
}
